import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { TopBar } from "../components/TopBar";
import { ConditionPill } from "../components/shared/ConditionPill";
import { conditionHref } from "../lib/links";
import { moodForSuccess } from "../lib/moods";
import type { ManifestSession } from "../models";
import { CONDITION_SHORT_NAMES } from "../models/condition-short-names";
import { useManifestStore } from "../state/manifestStore";
import { useSettings } from "../state/settingsStore";

type SortKey =
  | "started_at"
  | "title"
  | "project"
  | "user"
  | "turns"
  | "effort"
  | "success"
  | "issues";
type SortDir = "asc" | "desc";

interface SortState {
  key: SortKey;
  dir: SortDir;
}

const FILTER_KEYS = ["title", "project", "user", "turns", "effort", "success", "issue"] as const;
type FilterKey = (typeof FILTER_KEYS)[number];
type Filters = Record<FilterKey, string>;

const SEV_RANK = { high: 2, med: 1, low: 0 } as const;

export function Sessions() {
  const { tenant } = useParams();
  const manifest = useManifestStore((s) => s.manifest);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState<SortState>({ key: "issues", dir: "desc" });

  // Filters live in the URL so /sessions?project=x&issue=B05.02 deep-links
  // from Overview pills land with filters applied and bookmarks are stable.
  const filters: Filters = useMemo(() => {
    const f = {} as Filters;
    for (const k of FILTER_KEYS) f[k] = searchParams.get(k) ?? "";
    return f;
  }, [searchParams]);

  const syntheticOnly = useSettings((s) => s.syntheticOnly);
  const tenantSessions = useMemo(() => {
    if (!manifest || !tenant) return [];
    let arr = manifest.sessions.filter((s) => s.tenant === tenant);
    if (syntheticOnly) arr = arr.filter((s) => s.synthetic);
    return arr;
  }, [manifest, tenant, syntheticOnly]);

  const userNameById = useMemo(() => {
    if (!manifest) return new Map<string, string>();
    return new Map(manifest.users.map((u) => [u.id, u.name]));
  }, [manifest]);

  const projectNameById = useMemo(() => {
    if (!manifest) return new Map<string, string>();
    return new Map(manifest.projects.filter((p) => p.tenant === tenant).map((p) => [p.id, p.name]));
  }, [manifest, tenant]);

  const projectIds = useMemo(
    () => [...new Set(tenantSessions.map((s) => s.project))].sort(),
    [tenantSessions],
  );
  const userIds = useMemo(
    () => [...new Set(tenantSessions.map((s) => s.user))].sort(),
    [tenantSessions],
  );
  const issueCodes = useMemo(() => {
    const set = new Set<string>();
    for (const s of tenantSessions) {
      for (const c of s.top_conditions) set.add(c.code);
    }
    return [...set].sort();
  }, [tenantSessions]);

  const filtered = useMemo(
    () => tenantSessions.filter((s) => passesFilter(s, filters)),
    [tenantSessions, filters],
  );
  const sorted = useMemo(() => sortSessions(filtered, sort), [filtered, sort]);

  if (!manifest || !tenant) return null;

  const anyFilter = Object.values(filters).some((v) => v !== "");

  function toggleSort(key: SortKey) {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" },
    );
  }

  function setFilter(k: FilterKey, v: string) {
    const next = new URLSearchParams(searchParams);
    if (v) next.set(k, v);
    else next.delete(k);
    setSearchParams(next, { replace: true });
  }

  function clearAllFilters() {
    const next = new URLSearchParams(searchParams);
    for (const k of FILTER_KEYS) next.delete(k);
    setSearchParams(next, { replace: true });
  }

  return (
    <>
      <TopBar tenant={tenant} title="Sessions" />
      <div className="app-content page-fill">
        <div className="card scroll-card">
          <div className="card-header">
            <h2>
              {sorted.length} sessions
              {anyFilter ? (
                <span className="meta-label"> · filtered from {tenantSessions.length}</span>
              ) : null}
            </h2>
            {anyFilter ? (
              <button type="button" className="link-btn" onClick={clearAllFilters}>
                Clear filters
              </button>
            ) : (
              <span className="meta-label mono">click a header to sort</span>
            )}
          </div>
          <div className="table-scroll">
            <table className="roster sessions-table">
              <thead>
                <tr>
                  <th aria-label="status" />
                  <SortableTH label="When" k="started_at" sort={sort} onSort={toggleSort} />
                  <SortableTH label="Title" k="title" sort={sort} onSort={toggleSort}>
                    <input
                      className="col-filter"
                      type="text"
                      placeholder="search"
                      value={filters.title}
                      onChange={(e) => setFilter("title", e.target.value)}
                    />
                  </SortableTH>
                  <SortableTH label="Project" k="project" sort={sort} onSort={toggleSort}>
                    <select
                      className="col-filter"
                      value={filters.project}
                      onChange={(e) => setFilter("project", e.target.value)}
                    >
                      <option value="">all</option>
                      {projectIds.map((p) => (
                        <option key={p} value={p}>
                          {projectNameById.get(p) ?? p}
                        </option>
                      ))}
                    </select>
                  </SortableTH>
                  <SortableTH label="User" k="user" sort={sort} onSort={toggleSort}>
                    <select
                      className="col-filter"
                      value={filters.user}
                      onChange={(e) => setFilter("user", e.target.value)}
                    >
                      <option value="">all</option>
                      {userIds.map((u) => (
                        <option key={u} value={u}>
                          {userNameById.get(u) ?? u}
                        </option>
                      ))}
                    </select>
                  </SortableTH>
                  <SortableTH label="Turns" k="turns" sort={sort} onSort={toggleSort}>
                    <input
                      className="col-filter"
                      type="number"
                      min={0}
                      placeholder="≥"
                      value={filters.turns}
                      onChange={(e) => setFilter("turns", e.target.value)}
                    />
                  </SortableTH>
                  <SortableTH label="Effort" k="effort" sort={sort} onSort={toggleSort}>
                    <input
                      className="col-filter"
                      type="number"
                      min={0}
                      max={100}
                      placeholder="≥"
                      value={filters.effort}
                      onChange={(e) => setFilter("effort", e.target.value)}
                    />
                  </SortableTH>
                  <SortableTH label="Success" k="success" sort={sort} onSort={toggleSort}>
                    <input
                      className="col-filter"
                      type="number"
                      min={0}
                      max={100}
                      placeholder="≥"
                      value={filters.success}
                      onChange={(e) => setFilter("success", e.target.value)}
                    />
                  </SortableTH>
                  <SortableTH label="Issues" k="issues" sort={sort} onSort={toggleSort}>
                    <select
                      className="col-filter"
                      value={filters.issue}
                      onChange={(e) => setFilter("issue", e.target.value)}
                    >
                      <option value="">all</option>
                      {issueCodes.map((c) => (
                        <option key={c} value={c}>
                          {CONDITION_SHORT_NAMES[c] ?? c}
                        </option>
                      ))}
                    </select>
                  </SortableTH>
                </tr>
              </thead>
              <tbody>
                {sorted.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <span className={`dot mood-${moodForSuccess(s.scores.overall_success)}`} />
                    </td>
                    <td className="mono">{s.started_at.slice(0, 16).replace("T", " ")}</td>
                    <td>
                      <Link to={`/${tenant}/sessions/${s.id}`}>{s.title}</Link>
                    </td>
                    <td>{projectNameById.get(s.project) ?? s.project}</td>
                    <td>{userNameById.get(s.user) ?? s.user}</td>
                    <td className="mono">{s.turn_count}</td>
                    <td className="mono">{s.scores.human_effort}</td>
                    <td className="mono">{s.scores.overall_success}</td>
                    <td>
                      {s.top_conditions.slice(0, 3).map((c) => (
                        <ConditionPill
                          key={c.code}
                          code={c.code}
                          severity={c.severity}
                          to={conditionHref(tenant, c.code)}
                        />
                      ))}
                    </td>
                  </tr>
                ))}
                {sorted.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="empty">
                      No sessions match the current filters.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

function SortableTH({
  label,
  k,
  sort,
  onSort,
  children,
}: {
  label: string;
  k: SortKey;
  sort: SortState;
  onSort: (k: SortKey) => void;
  children?: React.ReactNode;
}) {
  const active = sort.key === k;
  const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "↕";
  return (
    <th aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        className={`sort-header ${active ? "active" : ""}`}
        onClick={() => onSort(k)}
      >
        <span>{label}</span>
        <span className={`sort-arrow ${active ? "active" : ""}`}>{arrow}</span>
      </button>
      {children}
    </th>
  );
}

function passesFilter(s: ManifestSession, f: Filters): boolean {
  if (f.title && !s.title.toLowerCase().includes(f.title.toLowerCase())) return false;
  if (f.project && s.project !== f.project) return false;
  if (f.user && s.user !== f.user) return false;
  if (f.turns) {
    const n = Number.parseInt(f.turns, 10);
    if (!Number.isNaN(n) && s.turn_count < n) return false;
  }
  if (f.effort) {
    const n = Number.parseInt(f.effort, 10);
    if (!Number.isNaN(n) && s.scores.human_effort < n) return false;
  }
  if (f.success) {
    const n = Number.parseInt(f.success, 10);
    if (!Number.isNaN(n) && s.scores.overall_success < n) return false;
  }
  if (f.issue && !s.top_conditions.some((c) => c.code === f.issue)) return false;
  return true;
}

function sortSessions(arr: ManifestSession[], s: SortState): ManifestSession[] {
  const out = [...arr];
  out.sort((a, b) => {
    const cmp = compare(a, b, s.key);
    return s.dir === "asc" ? cmp : -cmp;
  });
  return out;
}

function compare(a: ManifestSession, b: ManifestSession, key: SortKey): number {
  switch (key) {
    case "started_at":
      return a.started_at.localeCompare(b.started_at);
    case "title":
      return a.title.localeCompare(b.title);
    case "project":
      return a.project.localeCompare(b.project);
    case "user":
      return a.user.localeCompare(b.user);
    case "turns":
      return a.turn_count - b.turn_count;
    case "effort":
      return a.scores.human_effort - b.scores.human_effort;
    case "success":
      return a.scores.overall_success - b.scores.overall_success;
    case "issues": {
      const aMax = a.top_conditions.reduce((m, c) => Math.max(m, SEV_RANK[c.severity]), -1);
      const bMax = b.top_conditions.reduce((m, c) => Math.max(m, SEV_RANK[c.severity]), -1);
      if (aMax !== bMax) return aMax - bMax;
      return a.top_conditions.length - b.top_conditions.length;
    }
  }
}
